// src/components/Categories/CategoryDetails.js - Corrigido para suportar múltiplos perfis
import React, { useState, useEffect } from "react";
import { auth, db, storage } from "../../firebase/config";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { ref, getBlob } from "firebase/storage";
import { parseOFXContent } from "../../utils/OFXParser";
import { normalizeSicoobDescription } from "../../utils/patternRules";
import MainLayout from "../Layout/MainLayout";
import { useUserContext } from "../../contexts/UserContext";
import "./CategoryDetails.css";

// Importar serviços refatorados para garantir consistência
import { 
  loadAvailablePeriods,
  loadCategoryMappings,
  loadTransactionsForPeriod
} from "../../services/transactionRepository";

import { checkTransactionCategorization } from "../../services/categorizationService";

const CategoryDetails = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState("");
  const [categoryMappings, setCategoryMappings] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Obter o contexto completo do usuário incluindo perfis ativos
  const { 
    activeAccountType, 
    getAccountSubpath,
    activeBusinessProfileId,
    businessProfiles 
  } = useUserContext();
  
  const transactionsPerPage = 10;

  // Obter informações do perfil ativo para exibição
  const getActiveProfileInfo = () => {
    if (activeAccountType === 'personal') {
      return { name: 'Pessoal', type: 'personal' };
    } else {
      const activeProfile = businessProfiles.find(p => p.id === activeBusinessProfileId);
      return { 
        name: activeProfile?.profileName || 'Empresarial', 
        type: 'business',
        profileId: activeBusinessProfileId 
      };
    }
  };

  const activeProfileInfo = getActiveProfileInfo();

  // Carregar usuário, períodos e categorias
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        if (auth.currentUser) {
          setUser(auth.currentUser);
          
          // Usar o serviço refatorado para carregar períodos
          const availablePeriods = await loadAvailablePeriods();
          setPeriods(availablePeriods);
          
          // Verificar se existe um período salvo no localStorage
          let savedPeriod = localStorage.getItem('selectedPeriod');
          let periodToUse;
          
          if (savedPeriod && availablePeriods.find(p => p.value === savedPeriod)) {
            periodToUse = savedPeriod;
          } else if (availablePeriods.length > 0) {
            periodToUse = availablePeriods[0].value;
            localStorage.setItem('selectedPeriod', periodToUse);
          }
          
          if (periodToUse) {
            setSelectedPeriod(periodToUse);
          }
          
          // Carregar categorias do usuário usando a lógica correta de perfis
          await loadUserCategories();
          
          // Usar o serviço refatorado para carregar mapeamentos
          const mappings = await loadCategoryMappings();
          setCategoryMappings(mappings);
        }
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        setError("Não foi possível carregar os dados iniciais.");
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [activeAccountType, activeBusinessProfileId]); // Incluir activeBusinessProfileId como dependência

  // Carregar categorias do usuário com lógica correta de perfis
  const loadUserCategories = async () => {
    try {
      if (!auth.currentUser) return;
      
      const accountSubpath = getAccountSubpath();
      const isPersonal = activeAccountType === 'personal';
      
      let userCategoriesDocRef;
      
      if (isPersonal) {
        // Para conta pessoal, usar a subcoleção "personal/categories"
        userCategoriesDocRef = doc(db, "userCategories", auth.currentUser.uid, "personal", "categories");
      } else {
        // Para perfil empresarial, usar a lógica baseada no perfil ativo
        if (accountSubpath === 'business') {
          // Para o perfil principal, verificar primeiro no novo caminho estruturado
          userCategoriesDocRef = doc(db, "userCategories", auth.currentUser.uid, "business", "categories");
          
          // Verificar se o documento existe
          const docSnapshot = await getDoc(userCategoriesDocRef);
          
          // Se não existir, tentar o caminho legado (retrocompatibilidade)
          if (!docSnapshot.exists()) {
            console.log(`Documento de categorias não encontrado para perfil business, tentando caminho antigo...`);
            userCategoriesDocRef = doc(db, "userCategories", auth.currentUser.uid);
          }
        } else {
          // Para outros perfis empresariais, usar o ID do perfil
          userCategoriesDocRef = doc(db, "userCategories", auth.currentUser.uid, accountSubpath, "categories");
        }
      }
      
      const userCategoriesDoc = await getDoc(userCategoriesDocRef);
      
      if (userCategoriesDoc.exists()) {
        const categoriesData = userCategoriesDoc.data();
        
        // Process categories into a more usable format for the TransactionItem component
        if (categoriesData.categories) {
          // Extrair categorias selecionadas organizadas por grupo
          const flatCategories = [];
          
          Object.keys(categoriesData.categories).forEach(path => {
            if (categoriesData.categories[path]) {
              const parts = path.split('.');
              if (parts.length === 2) {
                const group = parts[0];
                const category = parts[1];
                
                flatCategories.push({
                  fullPath: path,
                  group,
                  category,
                  displayName: category
                });
              }
            }
          });
          
          // Ordenar categorias por grupo e depois por nome
          flatCategories.sort((a, b) => {
            if (a.group !== b.group) {
              return a.group.localeCompare(b.group);
            }
            return a.category.localeCompare(b.category);
          });
          
          setCategories(flatCategories);
          
          // Se houver categorias, pré-selecionar a primeira
          if (flatCategories.length > 0) {
            setSelectedCategory(flatCategories[0].fullPath);
          }
        }
      }
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      setError("Não foi possível carregar suas categorias.");
    }
  };

  // Efeito para carregar transações quando mudar período ou categoria
  useEffect(() => {
    if (selectedPeriod && selectedCategory) {
      loadTransactionsForCategory();
    }
  }, [selectedPeriod, selectedCategory, categoryMappings, activeAccountType, activeBusinessProfileId]);

  // Carregar transações para a categoria selecionada usando lógica correta de perfis
  const loadTransactionsForCategory = async () => {
    try {
      setLoading(true);
      
      if (!auth.currentUser || !selectedPeriod || !selectedCategory) return;
      
      // Extrair grupo e categoria da seleção
      const selectedCategoryParts = selectedCategory.split('.');
      if (selectedCategoryParts.length !== 2) return;
      
      const selectedGroup = selectedCategoryParts[0];
      const selectedCategoryName = selectedCategoryParts[1];
      
      // Usar o serviço refatorado para obter transações
      const { transactions: allTransactions } = await loadTransactionsForPeriod(selectedPeriod);
      
      const transactionsForCategory = [];
      let totalAmountForCategory = 0;
      
      // Identificar mapeamentos específicos por ID de transação
      const specificMappings = {};
      
      // Identificamos todos os mapeamentos específicos (para transações únicas)
      Object.entries(categoryMappings).forEach(([key, mapping]) => {
        if (mapping.isSpecificMapping && mapping.transactionId) {
          specificMappings[mapping.transactionId] = mapping;
        }
      });
      
      // Processar cada transação
      allTransactions.forEach(transaction => {
        const bankName = transaction.bankInfo?.org || "";
        const amount = transaction.amount || 0;
        
        // Verificar se é uma transação do SICOOB
        const isSicoob = 
          bankName.includes("SICOOB") || 
          bankName.includes("COOP DE CRED") ||
          transaction.description.toUpperCase().includes("SICOOB") || 
          transaction.description.toUpperCase().includes("SICREDI");
        
        // Aplicar normalização especial para SICOOB/SICREDI
        const normalizedDescription = isSicoob ? 
          normalizeSicoobDescription(transaction.description, bankName) : 
          transaction.description.trim().toLowerCase();
        
        // Adicionar prefixos baseados no sinal do valor
        const signPrefix = amount > 0 ? "positive_" : "negative_";
        const signedKey = signPrefix + normalizedDescription;
        
        // Criar chave específica para SICOOB
        const sicoobKey = isSicoob ? `sicoob_${normalizedDescription}` : null;
        
        let isCategorized = false;
        let mapping = null;
        
        // Primeiro verificar se existe um mapeamento específico para esta transação
        if (specificMappings[transaction.id]) {
          mapping = specificMappings[transaction.id];
          
          // Verificar se a transação pertence à categoria e grupo selecionados
          if (mapping.groupName === selectedGroup && mapping.categoryName === selectedCategoryName) {
            isCategorized = true;
          }
        } 
        // Verificar chaves específicas para SICOOB
        else if (isSicoob && sicoobKey && categoryMappings[sicoobKey]) {
          mapping = categoryMappings[sicoobKey];
          
          // Verificar se pertence à categoria e grupo selecionados
          if (mapping.groupName === selectedGroup && mapping.categoryName === selectedCategoryName) {
            isCategorized = true;
          }
        }
        // Verificar mapeamento usando chave com prefixo de sinal
        else if (categoryMappings[signedKey]) {
          mapping = categoryMappings[signedKey];
          
          // Verificar se não é um mapeamento específico para outra transação
          if (!mapping.isSpecificMapping) {
            // Verificar se pertence à categoria e grupo selecionados
            if (mapping.groupName === selectedGroup && mapping.categoryName === selectedCategoryName) {
              isCategorized = true;
            }
          }
        }
        // Verificar mapeamento normal (para compatibilidade com sistema legado)
        else if (categoryMappings[normalizedDescription]) {
          mapping = categoryMappings[normalizedDescription];
          
          // Verificar se não é um mapeamento específico para outra transação
          if (!mapping.isSpecificMapping) {
            // Verificar se pertence à categoria e grupo selecionados
            if (mapping.groupName === selectedGroup && mapping.categoryName === selectedCategoryName) {
              isCategorized = true;
            }
          }
        }
        
        // Verificar se a transação foi auto-categorizada com categoryPath
        if (!isCategorized && transaction.categoryPath) {
          const pathParts = transaction.categoryPath.split('.');
          if (pathParts.length >= 2) {
            const trnGroup = pathParts[0];
            const trnCategory = pathParts[1];
            
            if (trnGroup === selectedGroup && trnCategory === selectedCategoryName) {
              isCategorized = true;
              console.log(`Auto-categorized transaction found: ${transaction.description} (${transaction.categoryPath})`);
            }
          }
        }
        
        if (isCategorized) {
          transactionsForCategory.push({
            ...transaction,
            date: new Date(transaction.date),
            fileId: transaction.fileId || 'unknown',
            fileName: transaction.fileName || 'unknown',
            source: 'ofx'
          });
          
          totalAmountForCategory += transaction.amount;
        }
      });
      
      // Buscar e processar entradas de dinheiro para o período e categoria
      try {
        const accountSubpath = getAccountSubpath();
        
        const cashEntriesQuery = query(
          collection(db, "cashEntries", auth.currentUser.uid, accountSubpath),
          where("period", "==", selectedPeriod)
        );
        
        const cashEntriesSnapshot = await getDocs(cashEntriesQuery);
        
        // Processar cada entrada de dinheiro
        cashEntriesSnapshot.forEach(doc => {
          const entry = doc.data();
          
          // Verificar se a entrada tem categoria e valor
          if (entry.categoryPath && typeof entry.amount === 'number') {
            const pathParts = entry.categoryPath.split('.');
            
            if (pathParts.length >= 2) {
              const entryGroup = pathParts[0];
              const entryCategory = pathParts[1];
              
              // Verificar se a entrada corresponde à categoria selecionada
              if (entryGroup === selectedGroup && entryCategory === selectedCategoryName) {
                // Criar um objeto de transação com os dados da entrada de dinheiro
                const cashTransaction = {
                  id: doc.id,
                  date: entry.transactionDate ? new Date(entry.transactionDate) : entry.createdAt.toDate(),
                  description: entry.description || "Entrada de dinheiro",
                  amount: entry.amount,
                  category: selectedCategoryName,
                  categoryPath: entry.categoryPath,
                  groupName: entryGroup,
                  source: 'cash'
                };
                
                transactionsForCategory.push(cashTransaction);
                totalAmountForCategory += entry.amount;
                
                console.log(`Entrada de dinheiro encontrada para a categoria ${selectedCategoryName}: ${formatCurrency(entry.amount)}`);
              }
            }
          }
        });
      } catch (error) {
        console.error("Erro ao carregar entradas de dinheiro:", error);
      }
      
      // Ordenar transações por data (mais recente primeiro)
      transactionsForCategory.sort((a, b) => b.date - a.date);
      
      setTransactions(transactionsForCategory);
      setTotalAmount(totalAmountForCategory);
      
      // Calcular total de páginas
      setTotalPages(Math.max(1, Math.ceil(transactionsForCategory.length / transactionsPerPage)));
      setCurrentPage(1); // Resetar para a primeira página
    } catch (error) {
      console.error("Erro ao carregar transações:", error);
      setError("Não foi possível carregar as transações para esta categoria.");
    } finally {
      setLoading(false);
    }
  };

  // Função para formatar valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para formatar data
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('pt-BR').format(date);
  };

  // Função para mudar a página
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Função para alterar a categoria selecionada
  const handleCategoryChange = (event) => {
    const newCategory = event.target.value;
    setSelectedCategory(newCategory);
  };

  // Função para alterar o período
  const handlePeriodChange = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);
    
    // Salvar o período selecionado no localStorage
    localStorage.setItem('selectedPeriod', period);
  };

  // Obter transações para a página atual
  const getCurrentTransactions = () => {
    const startIndex = (currentPage - 1) * transactionsPerPage;
    const endIndex = startIndex + transactionsPerPage;
    return transactions.slice(startIndex, endIndex);
  };

  if (loading && (!selectedPeriod || categories.length === 0)) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando dados...</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout userName={user?.displayName || "Usuário"}>
      <div className="category-details-container">
        {/* Seletor de período com informações do perfil ativo */}
        <div className="period-selector-container">
          <div className="period-selector-header">
            <h2>
              Detalhes por Categoria - {activeProfileInfo.name}
              {activeProfileInfo.type === 'business' && activeProfileInfo.profileId && 
               activeProfileInfo.profileId !== 'business' && (
                <span className="profile-indicator"> (Perfil: {activeProfileInfo.profileId})</span>
              )}
            </h2>
            <div className="period-dropdown">
              <label htmlFor="period-select">Período: </label>
              <select
                id="period-select"
                value={selectedPeriod}
                onChange={handlePeriodChange}
                disabled={loading}
              >
                {periods.length === 0 ? (
                  <option value="">Nenhum período disponível</option>
                ) : (
                  periods.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <div className="category-filters">
          <div className="category-selector">
            <label htmlFor="category-select">Categoria:</label>
            <select
              id="category-select"
              value={selectedCategory}
              onChange={handleCategoryChange}
              disabled={loading}
            >
              {categories.length === 0 ? (
                <option value="">Nenhuma categoria disponível</option>
              ) : (
                categories.map((category) => (
                  <option key={category.fullPath} value={category.fullPath}>
                    {category.displayName}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
        
        <div className="category-details-summary">
          <div className="summary-card">
            <h3>Total da Categoria</h3>
            <div className={`summary-value ${totalAmount >= 0 ? "value-positive" : "value-negative"}`}>
              {formatCurrency(totalAmount)}
            </div>
          </div>
          
          <div className="summary-card">
            <h3>Número de Transações</h3>
            <div className="summary-value">
              {transactions.length}
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="loading-data">
            <div className="loading-spinner"></div>
            <p>Carregando transações...</p>
          </div>
        ) : (
          <>
            {transactions.length > 0 ? (
              <div className="transactions-content">
                <div className="transactions-table-container">
                  <table className="transactions-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Descrição</th>
                        <th>Valor</th>
                        <th>Fonte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getCurrentTransactions().map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{formatDate(transaction.date)}</td>
                          <td className="description-cell">
                            {transaction.description}
                          </td>
                          <td className={transaction.amount >= 0 ? "amount-positive" : "amount-negative"}>
                            {formatCurrency(transaction.amount)}
                          </td>
                          <td>
                            {transaction.source === 'cash' ? (
                              <span className="source-badge cash-source">Entrada Manual</span>
                            ) : (
                              <span className="source-badge ofx-source">Extrato</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Paginação */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className="pagination-button"
                      title="Primeira página"
                      aria-label="Ir para a primeira página"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pagination-icon">
                        <polyline points="11 17 6 12 11 7"></polyline>
                        <polyline points="18 17 13 12 18 7"></polyline>
                      </svg>
                    </button>
                    
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="pagination-button"
                      title="Página anterior"
                      aria-label="Ir para a página anterior"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pagination-icon">
                        <polyline points="15 18 9 12 15 6"></polyline>
                      </svg>
                    </button>
                    
                    <span className="pagination-info">
                      Página {currentPage} de {totalPages}
                    </span>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="pagination-button"
                      title="Próxima página"
                      aria-label="Ir para a próxima página"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pagination-icon">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                    
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className="pagination-button"
                      title="Última página"
                      aria-label="Ir para a última página"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pagination-icon">
                        <polyline points="13 17 18 12 13 7"></polyline>
                        <polyline points="6 17 11 12 6 7"></polyline>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="no-transactions">
                <p>Nenhuma transação encontrada para esta categoria no período selecionado.</p>
              </div>
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default CategoryDetails;